import React, { useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  format,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import { Plus } from "lucide-react";

import { statusMeta, DEFAULT_REASON_COLOR } from "./appointmentUtils";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const MonthView = ({ currentDate, appointments, onSelectAppointment, onSelectSlot }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const byDay = useMemo(() => {
    const map = new Map();
    appointments.forEach((appointment) => {
      const key = format(parseISO(appointment.scheduledAt), "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(appointment);
    });
    map.forEach((list) =>
      list.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
    );
    return map;
  }, [appointments]);

  const today = new Date();

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
      <div className="min-w-[640px]">
        <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="px-2 py-2 text-center text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayAppointments = byDay.get(key) || [];
          const outside = !isSameMonth(day, currentDate);
          const isToday = isSameDay(day, today);

          return (
            <div
              key={key}
              className={`group relative min-h-[7rem] border-b border-r border-slate-100 dark:border-slate-800 p-1.5 transition-colors ${
                outside ? "bg-slate-50/60 dark:bg-slate-950/30" : "bg-white dark:bg-slate-900"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded-full ${
                    isToday
                      ? "bg-indigo-600 text-white font-bold"
                      : outside
                      ? "text-slate-300 dark:text-slate-600"
                      : "text-slate-600 dark:text-slate-300 font-medium"
                  }`}
                >
                  {format(day, "d")}
                </span>
                <button
                  type="button"
                  onClick={() => onSelectSlot(day)}
                  title="Agendar cita este día"
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map((appointment) => {
                  const meta = statusMeta(appointment.status);
                  return (
                    <button
                      key={appointment.id}
                      type="button"
                      onClick={() => onSelectAppointment(appointment)}
                      className={`w-full flex items-center gap-1 px-1.5 py-1 rounded-md text-left text-[10px] leading-tight hover:brightness-95 transition-all ${
                        appointment.status === "cancelled" ? "opacity-50 line-through" : ""
                      }`}
                      style={{
                        backgroundColor: `${appointment.reason?.color || DEFAULT_REASON_COLOR}1a`,
                        borderLeft: `3px solid ${appointment.reason?.color || DEFAULT_REASON_COLOR}`,
                      }}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                      <span className="font-semibold text-slate-600 dark:text-slate-300 flex-shrink-0">
                        {format(parseISO(appointment.scheduledAt), "HH:mm")}
                      </span>
                      <span className="truncate text-slate-700 dark:text-slate-200">
                        {appointment.contact?.name}
                      </span>
                    </button>
                  );
                })}
                {dayAppointments.length > 3 && (
                  <p className="pl-1 text-[10px] font-medium text-slate-400">
                    +{dayAppointments.length - 3} más
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 dark:border-slate-800">
        {format(currentDate, "MMMM 'de' yyyy", { locale: es })}
      </div>
      </div>
    </div>
  );
};

export default MonthView;
